import {
    Component,
    EventEmitter,
    Input,
    Output,
    HostListener,
    inject,
    signal,
    computed,
    TemplateRef,
    OnInit,
    OnDestroy
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { AuthService } from '../../../core/services/auth.service';
import { BusinessContextService } from '../../../core/services/business-context.service';
import { SocketService } from '../../../core/services/socket.service';
import { ApiService } from '../../../core/services/api.service';

interface Notification {
    _id?: string;
    id?: string;
    type: string;
    category?: string;
    title: string;
    message: string;
    isNew: boolean;
    isRead?: boolean;
    isDismissed?: boolean;
    badge?: string;
    createdAt?: Date;
}

@Component({
    selector: 'app-admin-navbar',
    standalone: true,
    imports: [CommonModule, RouterLink, TranslateModule, ThemeToggleComponent],
    templateUrl: './app-navbar.html',
    styleUrls: ['./app-navbar.css']
})
export class AppNavbar implements OnInit {
    @Input() showToggle = true;
    @Input() toggleChecked = false;
    @Input() isMobileOver = false;
    @Input() sidenavCollapsed = false;
    @Output() toggleSidebarCollapseEvent = new EventEmitter<void>();
    @Output() toggleMobileNav = new EventEmitter<void>();
    @Output() logout = new EventEmitter<void>();

    private authService = inject(AuthService);
    public businessContext = inject(BusinessContextService);
    private socketService = inject(SocketService);
    private apiService = inject(ApiService);
    private router = inject(Router);
    private subscriptions: Subscription[] = [];

    businesses = signal<any[]>([]);

    // ── Notificaciones activas (panel campana) ───────────────────────────────
    notifications = signal<Notification[]>([]);

    // ── Historial (últimos 90 días, incluyendo descartadas) ──────────────────
    historyNotifications = signal<Notification[]>([]);
    historyLoading = signal(false);

    /** 'current' = panel de campana normal | 'history' = historial */
    notifView = signal<'current' | 'history'>('current');

    // ── Estados de carga locales (evitan bloquear toda la UI) ────────────────
    isClearing = signal(false);
    dismissingIds = signal<Set<string>>(new Set());
    restoringIds = signal<Set<string>>(new Set());

    ngOnInit() {
        if (this.authService.hasSession()) {
            this.loadBusinesses();
            this.loadCurrentNotifications();
        }
        this.initNotificationListeners();
    }

    private loadCurrentNotifications() {
        if (!this.authService.hasSession()) return;
        this.apiService.get<any>('notifications/my?limit=20').subscribe({
            next: (res: any) => {
                const data = Array.isArray(res) ? res : (res.data || []);
                this.notifications.set(data.map((n: any) => ({ ...n, isNew: !n.isRead })));
            },
            error: (err: any) => {
                console.error('Error cargando notificaciones', err);
                this.notifications.set([]);
            }
        });
    }

    loadHistory() {
        if (!this.authService.hasSession()) return;
        this.historyLoading.set(true);
        this.apiService.get<any>('notifications/history?limit=50').subscribe({
            next: (res: any) => {
                const data = Array.isArray(res) ? res : (res.data || []);
                this.historyNotifications.set(data.map((n: any) => ({ ...n, isNew: !n.isRead })));
                this.historyLoading.set(false);
            },
            error: (err: any) => {
                console.error('Error cargando historial', err);
                this.historyNotifications.set([]);
                this.historyLoading.set(false);
            }
        });
    }

    /** Alterna entre vista actual e historial dentro de la campana */
    toggleNotifView() {
        if (this.notifView() === 'current') {
            this.notifView.set('history');
            if (this.historyNotifications().length === 0) {
                this.loadHistory();
            }
        } else {
            this.notifView.set('current');
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    initNotificationListeners() {
        const userSub = this.socketService.onNotification().subscribe((notif: any) => this.addNotification(notif));
        const businessSub = this.socketService.onBusinessLog().subscribe((log: any) => this.addNotification(log));
        this.subscriptions.push(userSub, businessSub);
    }

    private addNotification(data: any) {
        const id = data._id || data.id;
        if (id && this.notifications().some(n => (n._id || n.id) === id)) return;
        const newNotif: Notification = {
            ...data,
            isNew: !data.isRead,
            isRead: !!data.isRead,
            createdAt: data.createdAt ? new Date(data.createdAt) : new Date()
        };
        // Actualizar síncronamente pero usando signal update para batching
        this.notifications.update(list => [newNotif, ...list].slice(0, 20));
    }

    markAsRead(notif: Notification) {
        if (notif.isRead) return;
        const id = notif._id || notif.id;
        if (!id) { notif.isRead = true; notif.isNew = false; return; }
        this.apiService.patch(`notifications/${id}/read`, {}).subscribe({
            next: () => { notif.isRead = true; notif.isNew = false; },
            error: () => { notif.isRead = true; notif.isNew = false; }
        });
    }

    /** Descarta UNA notificación del panel (soft-delete) */
    dismissOne(notif: Notification, event: Event) {
        event.stopPropagation();
        const id = (notif._id || notif.id) as string;
        if (!id) {
            this.notifications.update(list => list.filter(n => n !== notif));
            return;
        }

        // Marcar como en proceso localmente
        this.dismissingIds.update(set => { set.add(id); return new Set(set); });

        const headers = new HttpHeaders().set('x-skip-loading', 'true');

        this.apiService.patch(`notifications/${id}/dismiss`, {}, undefined, headers).subscribe({
            next: () => {
                this.notifications.update(list => list.filter(n => (n._id || n.id) !== id));
                this.dismissingIds.update(set => { set.delete(id); return new Set(set); });
            },
            error: () => {
                this.notifications.update(list => list.filter(n => (n._id || n.id) !== id));
                this.dismissingIds.update(set => { set.delete(id); return new Set(set); });
            }
        });
    }

    /** Restaura una notificación descartada desde el historial */
    restoreOne(notif: Notification, event: Event) {
        event.stopPropagation();
        const id = (notif._id || notif.id) as string;
        if (!id) return;

        this.restoringIds.update(set => { set.add(id); return new Set(set); });
        const headers = new HttpHeaders().set('x-skip-loading', 'true');

        this.apiService.patch(`notifications/${id}/restore`, {}, undefined, headers).subscribe({
            next: () => {
                // Actualizar en la lista del historial
                this.historyNotifications.update(list =>
                    list.map(n => (n._id || n.id) === id ? { ...n, isDismissed: false } : n)
                );
                // Refrescar panel actual para que aparezca
                this.loadCurrentNotifications();
                this.restoringIds.update(set => { set.delete(id); return new Set(set); });
            },
            error: () => {
                this.restoringIds.update(set => { set.delete(id); return new Set(set); });
            }
        });
    }

    /** Soft-delete masivo: descarta todas las del panel */
    clearAll() {
        if (this.notifications().length === 0 || this.isClearing()) return;

        this.isClearing.set(true);
        const headers = new HttpHeaders().set('x-skip-loading', 'true');

        this.apiService.patch('notifications/dismiss-all', {}, undefined, headers).subscribe({
            next: () => {
                this.notifications.set([]);
                if (this.notifView() === 'history') this.loadHistory();
                this.isClearing.set(false);
            },
            error: () => {
                this.notifications.set([]);
                this.isClearing.set(false);
            }
        });
    }

    loadBusinesses() {
        if (!this.authService.hasSession()) return;
        this.authService.getMyBusinesses().subscribe({
            next: (data: any[]) => this.businesses.set(Array.isArray(data) ? data : []),
            error: (err: any) => {
                console.error('Error cargando negocios', err);
                this.businesses.set([]);
            }
        });
    }

    onBusinessChangeDirect(business: any) {
        if (!business) { this.deselectBusiness(); return; }
        this.businessContext.selectBusiness(business);
        window.location.reload();
    }

    deselectBusiness() {
        this.businessContext.clearContext();
        this.router.navigate(['/app/select-business']);
    }

    activeMenu: 'menu' | 'profile' | 'business' | null = null;
    isSuperAdmin = computed(() => this.authService.currentUserValue?.isSuperAdmin === true);

    get user() { return this.authService.currentUserValue; }

    get userFullName(): string {
        const user = this.user as any;
        if (!user) return '';
        if (user.fullName) return user.fullName;
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Usuario';
    }

    get userInitials(): string {
        const user = this.authService.currentUserValue as any;
        if (!user) return 'U';
        const f = user.firstName?.[0] || '';
        const l = user.lastName?.[0] || '';
        return (f + l).toUpperCase() || user.username?.[0]?.toUpperCase() || 'U';
    }

    getBusinessInitials(name: string): string {
        if (!name) return 'B';
        return name.slice(0, 2).toUpperCase();
    }

    newNotificationsCount = computed(() => {
        return this.notifications().filter((n: Notification) => !n.isRead).length;
    });

    toggleMenu(menu: 'menu' | 'profile' | 'business') {
        if (this.activeMenu !== menu) {
            this.notifView.set('current'); // Reset view when reopening
        }
        this.activeMenu = this.activeMenu === menu ? null : menu;
    }

    onLogout() {
        this.activeMenu = null;
        this.logout.emit();
    }

    onToggleSidebar() {
        if (this.isMobileOver) { this.toggleMobileNav.emit(); }
        else { this.toggleSidebarCollapseEvent.emit(); }
    }

    getNotificationIcon(type: string): string {
        switch (type) {
            case 'error': return 'tabler:alert-circle';
            case 'warning': return 'tabler:alert-triangle';
            case 'success': return 'tabler:circle-check';
            default: return 'tabler:info-circle';
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target.closest('.menu-trigger') && !target.closest('.dropdown-menu')) {
            this.activeMenu = null;
        }
    }
}